import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { RadioFilterSelector } from '@frontend/analytics-shared';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, PopoverMenu, usePopoverMenu, NakedButton } from '@frontend/design-system';
import { trackingIds } from '../../../../tracking-ids';

type SortOption = {
  label: string;
  value: CallIntelTypes.CallIntelSortOption;
  trackingId: string;
};

type CallIntelSortFilterProps = {
  defaultSortOption?: CallIntelTypes.CallIntelSortOption;
  disabled?: boolean;
  onSortChange?: (sortOption: CallIntelTypes.CallIntelSortOption) => void;
  selectedSortOption?: CallIntelTypes.CallIntelSortOption;
  trackingId?: string;
  showRadioOptions?: boolean;
};

const sortOptions: SortOption[] = [
  {
    label: i18next.t('Last Call: Latest', { ns: 'call-intelligence' }),
    value: 'lastCallLatest',
    trackingId: trackingIds.callIntel.sortFilterLastCallLatest,
  },
  {
    label: i18next.t('Last Call: Oldest', { ns: 'call-intelligence' }),
    value: 'lastCallOldest',
    trackingId: trackingIds.callIntel.sortFilterLastCallOldest,
  },
  {
    label: i18next.t('Call Count: High to Low', { ns: 'call-intelligence' }),
    value: 'callCountHL',
    trackingId: trackingIds.callIntel.sortFilterCallCountHL,
  },
  {
    label: i18next.t('Call Count: Low to High', { ns: 'call-intelligence' }),
    value: 'callCountLH',
    trackingId: trackingIds.callIntel.sortFilterCallCountLH,
  },
];

const sortRadioOptions: Record<string, string> = sortOptions.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {} as Record<string, string>);

export const CallIntelSortFilter = ({
  defaultSortOption = 'lastCallLatest',
  disabled = false,
  selectedSortOption,
  onSortChange,
  trackingId,
  showRadioOptions = false,
}: CallIntelSortFilterProps) => {
  const { t } = useTranslation('call-intelligence');
  const { close: closePopover, getMenuProps, getTriggerProps } = usePopoverMenu({ placement: 'bottom' });
  const { ref: filterTriggerRef, ...triggerProps } = getTriggerProps();

  const [currentSortOption, setCurrentSortOption] = useState<CallIntelTypes.CallIntelSortOption>(defaultSortOption);

  const handleSortChange = (option: CallIntelTypes.CallIntelSortOption) => {
    setCurrentSortOption(option);
    onSortChange?.(option);
    closePopover();
  };

  useEffect(() => {
    if (selectedSortOption && selectedSortOption !== currentSortOption) {
      setCurrentSortOption(selectedSortOption);
    }
  }, [currentSortOption, selectedSortOption]);

  return (
    <div ref={filterTriggerRef}>
      {showRadioOptions ? (
        <RadioFilterSelector<CallIntelTypes.CallIntelSortOption>
          label={t('Sort')}
          onChange={handleSortChange}
          options={sortRadioOptions}
          showLabel={false}
          trackingId={trackingId}
          value={currentSortOption}
          withBorder={false}
          disabled={disabled}
        />
      ) : (
        <>
          <Chip.DropdownFilter
            {...triggerProps}
            css={styles.dropdownFilter(currentSortOption === defaultSortOption)}
            filterIsActive={currentSortOption !== defaultSortOption}
            leftElement={<Icon name='sort-descending-small' color='default' />}
            trackingId={trackingId}
          >
            {t('Sort')}
          </Chip.DropdownFilter>
          <PopoverMenu {...getMenuProps()}>
            {sortOptions.map((option) => (
              <NakedButton
                className={option.value === selectedSortOption ? 'active' : ''}
                css={styles.listItem}
                disabled={disabled}
                key={option.value}
                onClick={() => handleSortChange(option.value)}
                trackingId={option.trackingId}
              >
                {option.value === currentSortOption && (
                  <span style={{ color: theme.colors.text.interactive }}>
                    <Icon name='check' />
                  </span>
                )}
                {option.label}
              </NakedButton>
            ))}
          </PopoverMenu>
        </>
      )}
    </div>
  );
};

const styles = {
  dropdownFilter: (isDefault: boolean) => css`
    gap: ${theme.spacing(2)};
    max-width: 150px;
    width: 100%;
    background-color: ${isDefault ? 'white' : theme.colors.primary50};
    color: ${theme.colors.text.default};
  `,

  listItem: css`
    cursor: pointer;
    height: ${theme.spacing(5)};
    padding: ${theme.spacing(0, 3, 0, 6)};
    text-align: left;
    transition: background-color 0.3s ease;

    > span {
      left: ${theme.spacing(2)};
      position: absolute;
    }

    &.active,
    &:hover {
      background-color: ${theme.colors.neutral5};
    }
  `,
};
